import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDebounce, useDeepCompareEffect } from 'react-use';

import {
  CustomFieldStringAttributeValue,
  CustomFieldTextAttribute,
} from 'apis/customFields';
import { CustomFieldSuggestionDropdownButton } from 'components/conversation/conversationCustomFieldsForm/components/suggestions/customFieldSuggestionDropdownButton';
import { BaseCustomFieldText } from 'components/customFields/baseCustomFieldText/baseCustomFieldText';
import { errorNotification } from 'components/customFields/utils/notification';
import { useNotifications } from 'hooks/useNotifications';

import { ConversationCustomFieldBaseProps } from '../../conversationCustomFieldsForm.types';
import { getConversationCustomFieldsFormWarningMessage } from '../../utils/conversationCustomFieldsFormWarningMessage';
import { useSaveStatusIcon } from '../../utils/useSaveStatusIcon';

type CustomFieldTextProps = ConversationCustomFieldBaseProps<
  CustomFieldTextAttribute,
  CustomFieldStringAttributeValue
>;

const autoSaveDelay = 1000;

/** Renders a hook form text field to display/edit a custom field of type text. */
export const CustomFieldText = memo(function CustomFieldText({
  isAcw,
  customField,
  save,
  suggestions,
}: CustomFieldTextProps): JSX.Element {
  const { attribute, value } = customField;
  const isRequired = attribute.is_required;
  const { id, data_type } = attribute;
  const notify = useNotifications();
  const [icon, setStatus] = useSaveStatusIcon();
  const savedValue = value?.value ?? '';
  const formSavedValues = useMemo(
    () => ({ [id]: savedValue }),
    [id, savedValue],
  );
  const { control, getFieldState, reset, setValue } = useForm({
    mode: 'all',
    defaultValues: formSavedValues,
    shouldFocusError: false,
  });
  const fieldValue = useWatch({ control, name: id });

  useEffect(() => {
    if (isAcw) {
      // A trick to make the field `touched` and `dirty`, so that the warning
      // will be displayed when in wrap-up mode.
      // Using setTimeout because sometimes the field is not already rendered
      setTimeout(() => {
        setValue(id, savedValue, {
          shouldDirty: true,
          shouldTouch: true,
        });
      });
    }
  }, [id, isAcw, savedValue, setValue]);

  const changeValue = useCallback(async () => {
    if (savedValue === fieldValue) {
      return;
    }

    const fieldState = getFieldState(id);
    if (!fieldState.error) {
      const patch = {
        before: value?.value
          ? {
              [id]: {
                data_type,
                value: value?.value,
              },
            }
          : {},
        after: fieldValue
          ? {
              [id]: {
                data_type,
                value: fieldValue,
              },
            }
          : {},
      };

      setStatus('none');
      try {
        await save(attribute, patch);
        setStatus('success');
      } catch {
        reset(formSavedValues);
        setStatus('error');
        notify(errorNotification);
      }
    } else {
      setValue(id, savedValue);
      setStatus('error');
    }
  }, [
    attribute,
    data_type,
    fieldValue,
    formSavedValues,
    getFieldState,
    id,
    notify,
    reset,
    save,
    savedValue,
    setStatus,
    setValue,
    value?.value,
  ]);

  useDebounce(changeValue, autoSaveDelay, [fieldValue]);

  useDeepCompareEffect(() => {
    reset(formSavedValues);
  }, [formSavedValues]);

  return (
    <BaseCustomFieldText
      customField={customField}
      rules={{ required: isRequired }}
      control={control}
      componentProps={{
        icon,
        prefix: fieldValue.length === 0 &&
          suggestions?.length && {
            type: 'custom',
            noWrap: true,
            value: (
              <CustomFieldSuggestionDropdownButton
                suggestions={suggestions}
                setSuggestion={(suggestion) => {
                  setValue(id, suggestion[0], {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  return changeValue();
                }}
              />
            ),
          },
      }}
      warning={getConversationCustomFieldsFormWarningMessage(
        customField,
        isAcw,
      )}
    />
  );
});
